import { storage } from './storage';
import { checkFeatureEnabled } from './features';

const HISTORY_LIMIT = 20;

interface VariableHistory {
  [promptId: string]: {
    [variableName: string]: string[];
  };
}

/**
 * Add a variable value to history
 */
export async function addToVariableHistory(
  promptId: string,
  variableName: string,
  value: string
): Promise<void> {
  if (!(await checkFeatureEnabled('variableHistory'))) return;

  try {
    const history = ((await storage.get('variableHistory')) as VariableHistory) || {};

    // Initialize history structure if needed
    if (!history[promptId]) {
      history[promptId] = {};
    }
    if (!history[promptId][variableName]) {
      history[promptId][variableName] = [];
    }

    // Remove duplicate if exists
    const values = history[promptId][variableName];
    const index = values.indexOf(value);
    if (index !== -1) {
      values.splice(index, 1);
    }

    // Add new value to front
    values.unshift(value);

    // Trim to limit
    if (values.length > HISTORY_LIMIT) {
      values.length = HISTORY_LIMIT;
    }

    await storage.set('variableHistory', history);
  } catch (error) {
    console.error('Error saving variable history:', error);
  }
}

/**
 * Get variable history for a prompt
 */
export async function getVariableHistory(
  promptId: string,
  variableName: string
): Promise<string[]> {
  if (!(await checkFeatureEnabled('variableHistory'))) return [];

  try {
    const history = ((await storage.get('variableHistory')) as VariableHistory) || {};
    return history[promptId]?.[variableName] || [];
  } catch (error) {
    console.error('Error getting variable history:', error);
    return [];
  }
}

/**
 * Clear history for a specific prompt
 */
export async function clearPromptHistory(promptId: string): Promise<void> {
  try {
    const history = ((await storage.get('variableHistory')) as VariableHistory) || {};
    delete history[promptId];
    await storage.set('variableHistory', history);
  } catch (error) {
    console.error('Error clearing prompt history:', error);
  }
}

/**
 * Clear all variable history
 */
export async function clearAllHistory(): Promise<void> {
  try {
    await storage.set('variableHistory', {});
  } catch (error) {
    console.error('Error clearing all history:', error);
  }
}
